import { Button } from "@dropbox/dig-components/buttons";
import { Metadata } from "@dropbox/dig-components/metadata";
import { Table } from "@dropbox/dig-components/table";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { DashStackIcon } from "@mirage/mosaics/DCLWrappers/DashStackIcon";
import type { StackFilterOption } from "@mirage/service-settings/service/types";
import {
  StackSortDirection,
  StackSortOption,
  defaultStackPageSortPreference,
} from "@mirage/service-settings/service/types";
import { getTimeAgoString } from "@mirage/shared/util/time";
import { EMOJI_CLEARED_SENTINEL_VALUE } from "@mirage/stacks/Helpers/Utils";
import i18n, { meticulousRedactedStringClass } from "@mirage/translations";
import type { WorkingSetCardData } from "@mirage/working-set";
import { useState } from "react";
import AllStacksEmptyState from "../AllStacksGrid/EmptyStates/AllStacksEmptyState";
import { useAllStacksPerformanceTracking } from "../AllStacksGrid/useAllStacksPerformanceTracking";
import { useStackPagePreferences } from "../AllStacksGrid/useStackPagePreferences";
import styles from "./AllStacksEmbedList.module.css";

const AllStacksEmbedListHeader = () => {
  const { stackPageSortPreference, setStackPageSortPreference } =
    useStackPagePreferences();

  // Cycle through options
  const onClickHeader = (
    option: StackSortOption.ALPHA | StackSortOption.RECENT,
  ) => {
    let order = [StackSortDirection.ASC, StackSortDirection.DESC, undefined];
    if (option === StackSortOption.RECENT) {
      order = [StackSortDirection.DESC, StackSortDirection.ASC, undefined];
    }

    if (option === stackPageSortPreference?.option) {
      // This will always be ascending or descending
      const index = order.indexOf(stackPageSortPreference.direction);
      const direction = order[(index + 1) % order.length];
      setStackPageSortPreference(
        direction === undefined
          ? defaultStackPageSortPreference
          : { option, direction },
      );
    } else {
      setStackPageSortPreference({
        option,
        direction: order[0],
      });
    }
  };

  const isSortingByName =
    stackPageSortPreference?.option === StackSortOption.ALPHA &&
    stackPageSortPreference?.direction !== undefined;
  const isSortingByLastOpened =
    stackPageSortPreference?.option === StackSortOption.RECENT &&
    stackPageSortPreference?.direction !== undefined;

  const nameSortDirection = isSortingByName
    ? stackPageSortPreference?.direction === StackSortDirection.DESC
      ? "desc"
      : "asc"
    : "none";

  const lastOpenedSortDirection = isSortingByLastOpened
    ? stackPageSortPreference?.direction === StackSortDirection.DESC
      ? "desc"
      : "asc"
    : "none";

  return (
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell
          onClick={() => onClickHeader(StackSortOption.ALPHA)}
          sortDirection={nameSortDirection}
          width="40%"
        >
          <Text
            color={isSortingByName ? "standard" : "faint"}
            isBold={isSortingByName}
            size="small"
          >
            {i18n.t("all_stacks_embed_title_name")}
          </Text>
        </Table.HeaderCell>
        <Table.HeaderCell width="20%">
          <Text color="faint" size="small">
            {i18n.t("all_stacks_embed_title_type")}
          </Text>
        </Table.HeaderCell>
        <Table.HeaderCell width="20%">
          <Text color="faint" size="small">
            {i18n.t("all_stacks_embed_title_who_can_access")}
          </Text>
        </Table.HeaderCell>
        <Table.HeaderCell
          onClick={() => onClickHeader(StackSortOption.RECENT)}
          sortDirection={lastOpenedSortDirection}
          width="20%"
        >
          <Text
            color={isSortingByLastOpened ? "standard" : "faint"}
            isBold={isSortingByLastOpened}
            size="small"
          >
            {i18n.t("all_stacks_embed_title_last_opened")}
          </Text>
        </Table.HeaderCell>
      </Table.Row>
    </Table.Header>
  );
};

const AllStacksEmbedListRow = ({
  workingSetData,
  isMenuOpen,
  isHovered,
  setHovered,
}: {
  workingSetData: WorkingSetCardData;
  isMenuOpen: boolean;
  isHovered: boolean;
  setHovered: (isHovered: boolean) => void;
}) => {
  const showHoverState = isHovered || isMenuOpen;
  const updatedAtMs =
    (workingSetData.lastModifiedMs || workingSetData.maxHlcMs) ?? 0;

  return (
    <Table.Row
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        backgroundColor: showHoverState
          ? "var(--dig-color__background__subtle)"
          : undefined,
      }}
    >
      <Table.Cell>
        <Box display="flex" alignItems="center" columnGap="Micro Large">
          <DashStackIcon
            colorIndex={workingSetData.colorIndex}
            aria-label={i18n.t("aria_emoji_button")}
            emoji={workingSetData.emoji || EMOJI_CLEARED_SENTINEL_VALUE}
            size="large"
            customColor={workingSetData.customColor}
            style={{ flexShrink: 0 }}
          />
          <Button
            variant="transparent"
            hasNoUnderline
            onClick={workingSetData.onClickCard}
            onFocus={() => setHovered(true)}
          >
            <Text size="small" className={meticulousRedactedStringClass}>
              <Truncate
                maxWidth={250}
                tooltipControlProps={{ placement: "top", auto: true }}
              >
                {workingSetData.title}
              </Truncate>
            </Text>
          </Button>
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Metadata withDividers="bullet">
          <Metadata.Item>
            <Metadata.Label>{i18n.t("stack")}</Metadata.Label>
          </Metadata.Item>
          <Metadata.Item>
            <Metadata.Label>
              {i18n.t("num_items", {
                count: workingSetData.links.length,
              })}
            </Metadata.Label>
          </Metadata.Item>
        </Metadata>
      </Table.Cell>
      <Table.Cell>
        {workingSetData.memberCount > 1 &&
        !workingSetData.isCompanyPublishedStack &&
        !workingSetData.isPublicStack ? (
          workingSetData.getMembersComponent("small", false)
        ) : (
          <Text color="subtle" className={styles.accessIcon}>
            <UIIcon src={workingSetData.accessLevelIcon} />
            {workingSetData.accessLevel}
          </Text>
        )}
      </Table.Cell>
      <Table.Cell>
        {updatedAtMs !== 0 && (
          <Metadata>
            <Metadata.Item>
              <Metadata.Label>{getTimeAgoString(updatedAtMs)}</Metadata.Label>
            </Metadata.Item>
          </Metadata>
        )}
      </Table.Cell>
      {
        <Box
          // Show hover actions only when hovered
          display={showHoverState ? "flex" : "none"}
          // Show hover actions next to each other
          flexDirection="row"
          columnGap="Micro Medium"
          alignItems="center"
          // Show hover actions covering the right side of the row
          position="absolute"
          right="0"
          marginRight="Micro Medium"
          backgroundColor="Background Subtle"
          style={{ top: 24, zIndex: 1 }}
        >
          {workingSetData.embeddedActionMenuComponent}
        </Box>
      }
    </Table.Row>
  );
};

export const AllStacksEmbedList = ({
  workingSetCardData,
  filterPreference,
  menuOpenForStackId,
}: {
  workingSetCardData: WorkingSetCardData[];
  filterPreference?: StackFilterOption;
  menuOpenForStackId?: string;
}) => {
  useAllStacksPerformanceTracking();

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  if (workingSetCardData.length === 0) {
    return <AllStacksEmptyState filterPreference={filterPreference} />;
  }

  return (
    <Table spacing="xlarge">
      <AllStacksEmbedListHeader />
      <Table.Body>
        {workingSetCardData.map((workingSetData, index) => (
          <AllStacksEmbedListRow
            key={index}
            workingSetData={workingSetData}
            isMenuOpen={
              workingSetData.stack.namespace_id === menuOpenForStackId
            }
            isHovered={hoveredIndex === index}
            setHovered={(isHovered: boolean) =>
              setHoveredIndex(isHovered ? index : null)
            }
          />
        ))}
      </Table.Body>
    </Table>
  );
};
